// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Modal, Text } from '@mantine/core';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useListState, useDisclosure, UseListStateHandlers } from '@mantine/hooks';
import { Outlet } from 'react-router-dom';
import { CategoryDto, PositionDto } from '../../DocumentT';
import { openDeleteConfirmModal } from '../../../Atoms/DeleteConfirmModal';
import { PositionS } from '../../PositionS';
import { RoutingS } from '../../RoutingS';
import { ViolationContext } from '../ViolationContext';
import { LicenceS } from '../../LicenceS';
import PositionModal from '../../../Molecules/Document/Position/PositionModalV2';
import { isDefined } from '../../ValidationS';
import { useProductCategories } from '../../hooks/useProductCategories';
import { openCreateProductModal } from '../../../Molecules/Document/Position/AddProductModal';
import { ToastS } from '../../ToastS';
import { useOwnProfile } from '../ProfileContext';
import { useOwnContact } from '../ContactContext';
import { DataS } from '../../DataS';
import { FormatS } from '../../FormatS';

interface ProductsContextProps {
  loading: boolean;
  addingPosition: boolean;
  products: PositionDto[];
  adjustCategory: (category: string, newCategory?: string) => void;
  searchQuery: string;
  setQuery: (val: string) => void;
  deleteProduct: (positionId: number) => void;
  addProduct: (position?: Partial<PositionDto>) => void;
  editProduct: (positionId: number, patch: Partial<PositionDto>) => Promise<boolean>;
  openPositionModal: (position: PositionDto) => void;
  handleChange: (positionId: number, patch: Partial<PositionDto>) => void;
  refreshCategories: () => void;
  categories: CategoryDto[];
  loadingCategories: boolean;
  categoriesHandlers: UseListStateHandlers<CategoryDto>;
  removeGroup: (groupId: string) => void;
  currencySymbol: string;
}

export const ProductsContext = createContext({} as ProductsContextProps);

export const ProductsContextProvider = () => {
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const [loading, setLoading] = useState(false);
  const [addingPosition, setAdding] = useState(false);
  const { contact } = useOwnContact();
  const { profile } = useOwnProfile();
  const [products, handlers] = useListState<PositionDto>([]);
  const [searchQuery, setQuery] = useState<string>('');
  const [selectedPositionId, selectPosition] = useState<number>();
  const [opened, { toggle }] = useDisclosure(false);
  const { smallScaleBusiness } = profile ?? { smallScaleBusiness: false };
  const { country } = contact ?? {};
  const taxRates = useMemo(() => DataS.getTaxRatesByCountry(country, 19), [country]);
  const currencySymbol = useMemo(() => FormatS.getCountryCurrencySymbol(country), [country]);
  const { setState, prepend, filter, applyWhere } = handlers;
  const { categories, loading: loadingCategories, handlers: categoriesHandlers, refresh } = useProductCategories();

  useEffect(() => {
    RoutingS.changeTitle('Vordefinierte Produkte');
    setLoading(true);
    PositionS.fetchProducts()
      .then(setState)
      .finally(() => setLoading(false));
  }, [setState]);

  const deleteProduct = async (positionId: number) => {
    const onConfirm = async () => {
      const deleted = await PositionS.deleteProduct(positionId);
      if (deleted) {
        filter((p) => p.id !== positionId);
      }
      return deleted;
    };
    openDeleteConfirmModal({
      title: 'Position nicht mehr vorschlagen?',
      description: <Text>Soll Dir diese Position in Zukunft wirklich nicht mehr vorgeschlagen werden?</Text>,
      onConfirm,
    });
  };

  const editProduct = async (positionId: number, patch: Partial<PositionDto>) => {
    const resJSON = await PositionS.editProduct(positionId, patch);
    if (resJSON) {
      applyWhere(
        (item) => item.id === positionId,
        () => resJSON,
      );
      return true;
    }
    return false;
  };

  const handleChange = (positionId: number, patch: Partial<PositionDto>) => {
    applyWhere(
      (item) => item.id === positionId,
      (item) => ({ ...item, ...patch }),
    );
    if (patch.categoryId && !categories.map((c) => c.id).includes(patch.categoryId)) {
      // New category created --> Update UI accordingly
      refresh();
    }
  };

  const addProduct = async (position?: Partial<PositionDto>) =>
    executeWithLicenceCheck(LicenceS.Restrictions.PRODUCT_USAGE, () =>
      openCreateProductModal({
        onCreate: (pos) => prepend(pos),
        categories: categories.map((c) => c.name),
        position,
        smallScaleBusiness,
        taxRates,
        currencySymbol,
      }),
    );

  const openPositionModal = (position: PositionDto) => {
    selectPosition(position.id);
    toggle();
  };

  const adjustCategory = (category: string, newCategory?: string) => {
    applyWhere(
      (item) => item.category === category,
      (item) => ({ ...item, category: newCategory ?? null }),
    );
  };

  const removeGroup = (groupId: string) => {
    if (products.filter((p) => p.categoryId === groupId).length > 0) {
      ToastS.warn('foo', 'Kategorie mit vorhandenen Elementen kann nicht entfernt werden.');
      return;
    }
    const onConfirm = async () => {
      const deleted = await PositionS.deleteCategory(groupId);
      if (deleted) {
        categoriesHandlers.filter((c) => c.id !== groupId);
      }
      return deleted;
    };
    openDeleteConfirmModal({
      title: 'Kategory löschen',
      description: 'Möchten Sie die Kategorie unwiderruflich löschen?',
      onConfirm,
    });
  };

  const filteredProducts = useMemo(
    () =>
      products.filter(
        (p) => !isDefined(searchQuery) || (p.label && p.label.toLowerCase().includes(searchQuery.toLowerCase())),
      ),
    [products, searchQuery],
  );

  const value = useMemo(
    () => ({
      loading,
      addingPosition,
      products: filteredProducts,
      searchQuery,
      categories,
      loadingCategories,
      categoriesHandlers,
      refreshCategories: refresh,
      adjustCategory,
      setQuery,
      addProduct,
      editProduct,
      deleteProduct,
      openPositionModal,
      handleChange,
      removeGroup,
      currencySymbol,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, addingPosition, products, selectedPositionId, searchQuery, categories, loadingCategories, currencySymbol],
  );

  return (
    <ProductsContext.Provider value={value}>
      {selectedPositionId && (
        <Modal
          closeOnClickOutside={false}
          transition="slide-down"
          opened={opened}
          onClose={() => {
            selectPosition(undefined);
            toggle();
          }}
          title={
            <Text size="xl" weight="bolder">
              Position
            </Text>
          }
        >
          <PositionModal
            position={products.find((p) => p.id === selectedPositionId) as PositionDto}
            savePatch={(patch) => editProduct(selectedPositionId, patch)}
            refresh={(pos) => handleChange(selectedPositionId, pos)}
          />
        </Modal>
      )}
      <Outlet />
    </ProductsContext.Provider>
  );
};
